import { Button, Card, Empty, Flex, List, Modal, Select, Tag } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useCreateCourseTagsMutation,
  useDeleteCoursesHighlightsMutation,
  useGetCourseTagsQuery,
  useGetCoursesHighlightsQuery,
  useGetTagsQuery,
} from "../../../app/features/admin/admin.api";
import type { CoursesHighlightsResponse } from "../../../app/features/admin/admin.types";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import useModal from "../../../hooks/useModal";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import HighlightModal from "./_components/HighlightModal";
import TitleProgressbar from "./_components/TitleProgressbar";

export default function ManageHighlights() {
  const { courseId } = useParams();
  const courseIdNum = courseId ? Number(courseId) : undefined;
  const navigate = useNavigate();

  const { data: highlights = [], isLoading } = useGetCoursesHighlightsQuery(
    { id: courseIdNum as number },
    { skip: !courseIdNum },
  );

  const { open, handleOpen, handleClose } = useModal<any>();
  const [deleteHighlight] = useDeleteCoursesHighlightsMutation();

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteHighlight({ id }).unwrap();
          toast("success", "Deleted successfully");
        } catch (error: any) {
          toast("error", error?.data?.message || "Failed to delete highlight");
        }
      },
    });
  };

  return (
    <DashboardLayout title="Dashboard">
      <TitleProgressbar step={1} />

      <div className="grid md:grid-cols-2 gap-4">
        <HighlightItem
          title="What Learners Will Learn"
          type="LEARNING"
          highlights={highlights}
          loading={isLoading}
          onOpen={handleOpen}
          onDelete={handleDelete}
          courseId={courseIdNum}
        />
        <HighlightItem
          title="Who This Course Is For"
          type="AUDIENCE"
          highlights={highlights}
          loading={isLoading}
          onOpen={handleOpen}
          onDelete={handleDelete}
          courseId={courseIdNum}
        />
        <HighlightItem
          title="This Course Includes"
          type="INCLUDES"
          highlights={highlights}
          loading={isLoading}
          onOpen={handleOpen}
          onDelete={handleDelete}
          courseId={courseIdNum}
        />

        {/* Tag components */}
        <TagManager courseId={courseIdNum} />
      </div>

      <Flex justify="space-between" align="center" className="mt-6">
        <Button
          type="default"
          className="btn-bordered-primary border
               border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200 min-w-[100px]"
          onClick={() => navigate(`/admin/my-course/manage-course`)}
        >
          Back
        </Button>
        <Button
          type="primary"
          className="min-w-[100px]"
          onClick={() =>
            navigate(`/admin/my-course/manage-course/content/${courseIdNum}`)
          }
        >
          Next
        </Button>
      </Flex>

      {(open?.type === "ADD" || open?.type === "UPDATE") && (
        <HighlightModal
          open={open.open}
          onCancel={handleClose}
          initialValues={open?.data?.initialValues ?? null}
          type={open?.type}
          // pass meta to modal so it can include courseId and highlight type when creating
          // e.g. handleOpen({ type: 'ADD', data: { meta: { courseId, type } } })
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          meta={open?.data?.meta}
        />
      )}
    </DashboardLayout>
  );
}

type HighlightItemProps = {
  title: string;
  type: "LEARNING" | "AUDIENCE" | "INCLUDES" | "REQUIREMENT";
  highlights: CoursesHighlightsResponse[];
  loading?: boolean;
  onOpen: (payload: any) => void;
  onDelete: (id: number) => void;
  courseId?: number | undefined;
};

const HighlightItem = ({
  title,
  type,
  highlights,
  loading,
  onOpen,
  onDelete,
  courseId,
}: HighlightItemProps) => {
  const items = highlights?.filter((h) => h.type === type) ?? [];

  const metaCourseId = courseId ?? items?.[0]?.courseId;

  return (
    <Card className="rounded-md shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-theme-slate-700 text-sm font-medium">{title}</h3>
        <Button
          size="small"
          type="primary"
          onClick={() =>
            onOpen({
              type: "ADD",
              data: { meta: { courseId: metaCourseId, type, title } },
            })
          }
        >
          <AddIcon /> Add
        </Button>
      </div>

      <List
        loading={loading}
        dataSource={items}
        locale={{ emptyText: <Empty /> }}
        className="max-h-[300px] overflow-y-auto"
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button
                key={`edit-${item.id}`}
                size="small"
                onClick={() =>
                  onOpen({
                    type: "UPDATE",
                    data: {
                      initialValues: item,
                      meta: { courseId: item.courseId, type, title },
                    },
                  })
                }
                icon={<EditIcon />}
              />,
              <Button
                key={`del-${item.id}`}
                danger
                size="small"
                onClick={() => onDelete(item.id)}
                icon={<DeleteIcon />}
              />,
            ]}
          >
            <List.Item.Meta
              title={item.text}
              description={`Sort order: ${item.sortOrder}`}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

type TagManagerProps = {
  courseId?: number;
};

const TagManager = ({ courseId }: TagManagerProps) => {
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  // Fetch all available tags
  const { data: allTags = [], isLoading: isLoadingAllTags } = useGetTagsQuery();

  // Fetch course-specific tags
  const { data: courseTags = [], isLoading: isLoadingCourseTags } =
    useGetCourseTagsQuery(
      { courseId: courseId as number },
      { skip: !courseId },
    );

  const [createCourseTags, { isLoading: isCreating }] =
    useCreateCourseTagsMutation();

  const handleAddTags = async () => {
    if (!courseId || selectedTags.length === 0) return;

    try {
      await createCourseTags({
        courseId,
        body: { tagIds: selectedTags },
      }).unwrap();
      toast("success", "Tags added successfully");
      setSelectedTags([]);
    } catch (error: any) {
      toast("error", error?.data?.message || "Failed to add tags");
    }
  };

  // Get IDs of tags already assigned to the course
  const assignedTagIds = courseTags.map((tag) => tag.id);

  // Filter out tags that are already assigned
  const availableTags = allTags.filter(
    (tag) => !assignedTagIds.includes(tag.id),
  );

  return (
    <Card className="rounded-md shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-theme-slate-700 text-sm font-medium">
          Course Tags
        </h3>
      </div>

      <div className="space-y-4">
        {/* Display current tags */}
        <Flex align="center" gap={8}>
          <p className="text-sm text-gray-600">Current Tags:</p>
          {isLoadingCourseTags ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : courseTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {courseTags.map((tag) => (
                <Tag key={tag.Tag.id} color="blue">
                  {tag.Tag.name}
                </Tag>
              ))}
            </div>
          ) : (
            <Empty description="No tags assigned" />
          )}
        </Flex>

        {/* Add new tags */}
        <div>
          <p className="text-sm text-gray-600 mb-2">Add Tags:</p>
          <div className="flex gap-2">
            <Select
              mode="multiple"
              showSearch
              placeholder="Search and select admin tags"
              value={selectedTags}
              onChange={setSelectedTags}
              loading={isLoadingAllTags}
              className="flex-1"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={availableTags.map((tag) => ({
                label: tag.name,
                value: tag.id,
              }))}
            />
            <Button
              type="primary"
              onClick={handleAddTags}
              loading={isCreating}
              disabled={selectedTags.length === 0 || !courseId}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
